import { Feature, View, Map, MapBrowserEvent } from "ol";
import {
  ImageArcGISRest,
  OSM,
  Vector as VectorSource,
  Cluster as ClusterSource,
} from "ol/source";
import { Circle, SimpleGeometry } from "ol/geom";
import { Style } from "ol/style";
import { TileWMS } from "ol/source";
import { Tile as TileLayer, Vector as VectorLayer, Layer } from "ol/layer";
import { config } from "./config";
import { getClusterSymbol, getSymbol } from "./symbols";
import { SelectedStatus } from "./enums";
import { defaults as defaultControls } from "ol/control";
import { BasemapButton } from "./basemapButton";
import { LocationButton } from "./GeoLocation";
import { HomeButton } from "./Home";
import { getMap } from "./mapProvider";
import { extend, Extent, buffer } from "ol/extent";
import { getZoomButtons } from "./Zoom";
import { getUrlParam } from "./utils";

const clusterLayerId = config.defaults.clusterLayerId;
const tracksLayerId = config.defaults.tracksLayerId;

let hoverId = undefined;

const getLocationParam = () => {
  const loc = getUrlParam("loc");
  const locData = loc && loc.split(";");
  if (!loc || !locData || locData.length !== 3) {
    return;
  }

  return {
    x: parseInt(locData[0], 10),
    y: parseInt(locData[1], 10),
    zoom: parseInt(locData[2], 10),
  };
};

const getBuffer = (feature: Feature) =>
  buffer(feature.getGeometry().getExtent(), 100);

export const createMap = (
  target: HTMLDivElement,
  clusterSourceRef,
  texts,
  startPosition: { x: number; y: number; zoom: number }
): Map => {
  const locationParam = getLocationParam();
  const center = locationParam
    ? [locationParam.x, locationParam.y]
    : startPosition
    ? [startPosition.x, startPosition.y]
    : config.defaults.center;
  const zoom =
    locationParam?.zoom || startPosition?.zoom || config.defaults.zoom;

  const osmLayer = new TileLayer({
    source: new OSM(),
  });
  const ortoLayer = new TileLayer({
    source: new TileWMS({
      url: "https://lvmgeoserver.lvm.lv/geoserver/ows",
      params: {
        LAYERS: "public:Orto_LKS",
        FORMAT: "image/vnd.jpeg-png8",
      },
      serverType: "geoserver",
      crossOrigin: "anonymous",
    }),
    visible: false,
  });

  const clusterLayer = new VectorLayer({
    properties: { id: clusterLayerId },
    source: new ClusterSource({
      distance: 0,
      source: clusterSourceRef,
    }),
    style: (clusterFeature) => {
      const clusterFeatures = clusterFeature.get("features");
      return clusterFeatures.length > 1
        ? getClusterSymbol(clusterFeatures.length)
        : getSymbol(clusterFeatures[0].get("SELECTED"));
    },
  });
  // @ts-ignore
  window.clusterLayer = clusterLayer;

  return new Map({
    layers: [osmLayer, ortoLayer, clusterLayer],
    target,
    view: new View({
      projection: "EPSG:3857",
      center,
      zoom,
      extent: config.viewExtent,
    }),
    controls: defaultControls({ zoom: false, rotate: false }).extend([
      new BasemapButton(texts.button.basemap),
      new LocationButton(),
      new HomeButton(texts.button.home),
      getZoomButtons(),
    ]),
  });
};

export const onPointerMove = (
  evt: any,
  map: Map,
  target: HTMLDivElement
): void => {
  if (evt.dragging) {
    return;
  }

  const hasFeatureAtPixel = map.hasFeatureAtPixel(evt.pixel, {
    layerFilter: (layer: Layer) => layer.get("id") === clusterLayerId,
  });

  target.style.cursor = hasFeatureAtPixel ? "pointer" : null;

  if (hoverId !== undefined) {
    hoverId = undefined;
  }
};

export const getLayerById = (id: string): VectorLayer<VectorSource> =>
  getMap() &&
  ((getMap().getLayers().getArray() as Layer[]).find(
    (layer: Layer) => layer.get("id") === id
  ) as VectorLayer<VectorSource>);

export const forEachFeatureOfLayer = (
  layerId: string,
  func: CallableFunction
): void => {
  const layer = getLayerById(layerId) as VectorLayer<VectorSource>;
  layer && layer.getSource().forEachFeature((f: Feature) => func(f));
};

export const toggleMapControls = (isOn: boolean): void => {
  const basemap = document.getElementById("basemap-toggle");
  basemap && (basemap.style.display = isOn ? "none" : "block");
  const home = document.getElementById("home-button");
  home && (home.style.display = isOn ? "none" : "block");
};

export const zoomToFeature = (
  feature: Feature,
  callback?: (arg0: boolean) => void
): void => {
  if (!feature) {
    return;
  }
  const zoomToLevel = config.defaults.zoomToLevel;
  const view = getMap().getView();
  const geometry = feature.getGeometry() as SimpleGeometry;
  const currentZoom = view.getZoom();
  const maxZoom = currentZoom > zoomToLevel ? currentZoom : zoomToLevel;
  document.dispatchEvent(new Event("stopTracking"));
  view.fit(geometry, {
    duration: 500,
    maxZoom,
    padding: [0, 0, 0, view.get("paddingLeft")],
    callback,
  });
};

export const zoomToExtent = (
  extent: Extent,
  callback?: (arg0: boolean) => void
) => {
  document.dispatchEvent(new Event("stopTracking"));
  const view = getMap().getView();
  view.fit(extent, {
    duration: 500,
    padding: [50, 50, 50, 50 + view.get("paddingLeft")],
    callback,
  });
};

export const handleClusterClick = (clusterFeature: Feature) => {
  //@ts-ignore
  const extent = clusterFeature
    .get("features")
    .reduce<Extent>((result, feature) => {
      return result === null
        ? getBuffer(feature)
        : extend(result, getBuffer(feature));
    }, null);
  const view = getMap().getView();
  view.fit(extent, {
    padding: [0, 0, 0, view.get("paddingLeft")],
    duration: 500,
  });
};

export const selectTrack = (objectId: string) => {
  const layer = getLayerById(tracksLayerId) as VectorLayer<VectorSource>;
  layer &&
    layer.getSource().forEachFeature((trackFeature: Feature) => {
      trackFeature.get("TOURISMPLACEID") === objectId
        ? trackFeature.set("SELECTED", SelectedStatus.Selected)
        : trackFeature.get("SELECTED") !== SelectedStatus.Default &&
          trackFeature.set("SELECTED", SelectedStatus.Default);
    });
};

export const getNearbyCircle = (location: number[]): Feature => {
  const circle = new Feature({
    geometry: new Circle([location[0], location[1]], 60000),
  });
  circle.setStyle(
    new Style({
      renderer(coordinates, state) {
        //@ts-ignore
        const [[x, y], [x1, y1]] = coordinates;
        const ctx = state.context;
        const dx = x1 - x;
        const dy = y1 - y;
        const radius = Math.sqrt(dx * dx + dy * dy);

        const innerRadius = 0;
        const outerRadius = radius * 1.4;

        const gradient = ctx.createRadialGradient(
          x,
          y,
          innerRadius,
          x,
          y,
          outerRadius
        );
        gradient.addColorStop(0, "rgba(255,255,255,0)");
        gradient.addColorStop(0.6, "rgba(255,255,255,0.2)");
        gradient.addColorStop(1, "rgba(255,255,255,0.4)");
        ctx.beginPath();
        ctx.arc(x, y, radius, 0, 2 * Math.PI, true);
        ctx.fillStyle = gradient;
        ctx.fill();

        ctx.arc(x, y, radius, 0, 2 * Math.PI, true);
        ctx.strokeStyle = "rgba(171, 183, 191, 0.5)";
        ctx.stroke();
      },
    })
  );
  return circle;
};
